import React, { useContext, useEffect, useReducer, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { UserContext } from "../../context";
import { isNumeric } from "../../utility";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

export default function MarkOutDialog(props) {
    const [rowToEdit, setRowToEdit] = useState();
    const [inTime, setInTime] = useState(0);
    const [outTime, setOutTime] = useState(0);
    const [name, setName] = useState("");
    const [notes, setNotes] = useState([""]);
    const [stars, setStars] = useState(0);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
        useState(false);
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");
    const { currentUserData } = useContext(UserContext);
    const [, forceUpdate] = useReducer((value) => value + 1, 0);

    useEffect(() => {
        if (props.rowToEdit) {
            const row = props.rowToEdit;
            setRowToEdit(row);
            setInTime(row.inTime);
            setOutTime(row.outTime);
            setName(row.name);
            setNotes(row.notes.length === 0 ? [""] : row.notes);
            setStars(row.stars);
        } else {
            setRowToEdit(null);
            setName("");
            setNotes([""]);
            setStars(0);
        }
    }, [props.rowToEdit]);

    function resetAndClose() {
        props.onClose();
        setName("");
        setStars(0);
        setInTime(0);
        setOutTime(0);
        setNotes([""]);
    }

    function showStatusMessage(message, type) {
        setStatusType(type);
        setStatusMessage(message);
        setStatusIsVisible(true);
    }

    function handleTextChange(event) {
        if (event.target.id === "Mark Name") {
            setName(event.target.value);
        } else if (event.target.id === "In Time") {
            setInTime(event.target.value);
        } else if (event.target.id === "Out Time") {
            setOutTime(event.target.value);
        } else if (isNumeric(event.target.id)) {
            notes[event.target.id] = event.target.value;
            forceUpdate();
        }
    }

    function handleDoneClick() {
        if (name === "") {
            showStatusMessage("Name can't be blank", "error");
            return;
        }
        if (!isNumeric(inTime.toString())) {
            showStatusMessage("In Time must be a number", "error");
            return;
        }
        if (!isNumeric(outTime.toString())) {
            showStatusMessage("Out Time must be a number", "error");
            return;
        }
        if (inTime < 0) {
            showStatusMessage("In time cannot be below zero", "error");
            return;
        }
        if (outTime < 0) {
            showStatusMessage("Out time cannot be below zero", "error");
            return;
        }
        if (inTime > outTime) {
            showStatusMessage(
                "In Time must be smaller than Out Time",
                "error",
            );
            return;
        }

        const populatedNotes = notes.filter((note) => note !== "");

        if (rowToEdit) {
            rowToEdit.inTime = inTime;
            rowToEdit.outTime = outTime;
            rowToEdit.name = name;
            rowToEdit.notes = populatedNotes;
            rowToEdit.stars = stars;
            props.onEdit(rowToEdit);
        } else {
            props.onAdd(name, populatedNotes, stars);
        }

        setName("");
        setNotes([""]);
        setStars(0);
        setInTime(0);
        setOutTime(0);
    }

    return (
        <>
            <Dialog
                open={props.open}
                onClose={resetAndClose}
                fullWidth
                maxWidth="sm"
                aria-labelledby="clip-details-dialog-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: "92vh",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "rgba(0, 149, 61, 0.09)",
                        }}
                    >
                        <EditNoteOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            id="clip-details-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.15 }}
                        >
                            {rowToEdit ? "Edit clip" : "Add clip"}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Name the moment, add notes and choose its rating.
                        </Typography>
                    </Box>
                    {rowToEdit && (
                        <Tooltip title="Delete clip">
                            <IconButton
                                onClick={() =>
                                    setConfirmDeleteDialogOpen(true)
                                }
                                sx={{ color: "error.main", mt: -0.75 }}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        aria-label="Close clip details"
                        onClick={resetAndClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 3 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        {rowToEdit && (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "repeat(2, minmax(0, 1fr))",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                <TextField
                                    id="In Time"
                                    label="In time"
                                    value={inTime}
                                    onChange={handleTextChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                sec
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    id="Out Time"
                                    label="Out time"
                                    value={outTime}
                                    onChange={handleTextChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                sec
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        )}

                        <TextField
                            id="Mark Name"
                            label="Clip name"
                            value={name}
                            onChange={handleTextChange}
                            autoFocus
                            fullWidth
                        />

                        {notes.map((note, index) => (
                            <TextField
                                key={index}
                                id={index.toString()}
                                label={
                                    notes.length > 1
                                        ? `Note ${index + 1}`
                                        : "Note"
                                }
                                value={notes[index]}
                                onChange={handleTextChange}
                                multiline
                                minRows={2}
                                fullWidth
                            />
                        ))}

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 1, fontWeight: 800 }}
                            >
                                Rating
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                }}
                            >
                                {[1, 2, 3].map((rating) => (
                                    <IconButton
                                        key={rating}
                                        aria-label={`${rating} star rating`}
                                        onClick={() => setStars(rating)}
                                        sx={{
                                            color: "primary.main",
                                            p: 0.5,
                                        }}
                                    >
                                        {rating <= stars ? (
                                            <StarIcon />
                                        ) : (
                                            <StarBorderIcon />
                                        )}
                                    </IconButton>
                                ))}
                                {stars > 0 && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={() => setStars(0)}
                                        sx={{ ml: 1 }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}
                >
                    <Button onClick={resetAndClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleDoneClick}
                    >
                        {rowToEdit ? "Save changes" : "Add clip"}
                    </Button>
                </DialogActions>
            </Dialog>

            {confirmDeleteDialogOpen && (
                <ConfirmDeleteDialog
                    title="Delete Clip"
                    message="Are you sure you want to delete this clip?"
                    open={confirmDeleteDialogOpen}
                    onClose={(result) => {
                        if (result === true && currentUserData) {
                            props.onDelete(rowToEdit);
                        }
                        setConfirmDeleteDialogOpen(false);
                    }}
                />
            )}
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={(event, reason) => {
                    if (reason !== "clickaway") {
                        setStatusIsVisible(false);
                    }
                }}
            >
                <Alert
                    onClose={() => setStatusIsVisible(false)}
                    severity={statusType}
                >
                    {statusMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
